import { Link } from "react-router";

import { EarningsCardAmounts } from "./EarningsCardAmounts";
import { EarningsCardRailSection } from "./EarningsCardRailSection";

import { Column } from "@/components/Column";
import { ErrorText } from "@/components/ErrorText";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useGetStafferEarnings } from "@/hooks/queries/useGetStafferEarnings";
import { earningsProgress } from "@/lib/earningsProgress";
import { getJobLink } from "@/lib/notificationLinks";
import { EarningsJob } from "@/types";

const HIGHEST_VALUE_REQ_COUNT = 6;

export const EarningsHighestValueReqsSection = ({
  selectedUserId,
}: {
  selectedUserId?: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: earnings,
    isPending,
    isError,
  } = useGetStafferEarnings({ userId: selectedUserId });

  /* Helpers */
  // Each job's per-req commission is its revenue estimate stacked on top of
  // the current YTD basis, blended across any tier boundary it crosses.
  const getJobCommission = (job: EarningsJob): number => {
    if (!earnings) return 0;

    return earningsProgress.commissionBetween(
      earnings.totalRevenueYtd,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      earnings.totalRevenueYtd + (job.estimatedRevenueAmount || 0),
      earnings.commissionTiers,
    );
  };

  /* Render */
  const renderSkeleton = () => (
    <Column className="gap-3">
      <Skeleton className="h-3 w-44" />
      <Row className="gap-4">
        <Skeleton className="h-36 w-80 rounded-lg" />
        <Skeleton className="h-36 w-80 rounded-lg" />
      </Row>
    </Column>
  );

  const render = () => {
    if (isPending) {
      return renderSkeleton();
    }

    if (isError) {
      return <ErrorText errorMessage="Failed to load highest value reqs." />;
    }

    // Only jobs the staffer can earn new commission on qualify
    const jobs = earnings.jobs
      .filter((job) => job.earnableReqCount > 0)
      .sort(
        (a, b) =>
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          (b.estimatedRevenueAmount || 0) - (a.estimatedRevenueAmount || 0),
      )
      .slice(0, HIGHEST_VALUE_REQ_COUNT);

    // No earnable reqs hides the whole section
    if (jobs.length === 0) {
      return null;
    }

    const commissionTotal = jobs.reduce(
      (total, job) => total + getJobCommission(job),
      0,
    );

    return (
      <EarningsCardRailSection
        cardCount={jobs.length}
        commissionTotal={commissionTotal}
        label={`${jobs.length.toString()} highest value ${
          jobs.length === 1 ? "req" : "reqs"
        }`}
      >
        {jobs.map((job) => (
          <HighestValueReqCard
            key={job.jobId}
            commissionDollars={getJobCommission(job)}
            job={job}
          />
        ))}
      </EarningsCardRailSection>
    );
  };

  return render();
};

const HighestValueReqCard = ({
  commissionDollars,
  job,
}: {
  commissionDollars: number;
  job: EarningsJob;
}) => {
  /* Render */
  const render = () => (
    <Card className="w-80 flex-none p-5 shadow-none">
      <Column className="h-full gap-4">
        <Column className="min-w-0 gap-0.5">
          <Link
            className="w-fit max-w-full"
            to={getJobLink({ companyId: job.companyId, jobId: job.jobId })}
          >
            <Text className="truncate text-sm font-medium text-slate-900 hover:underline">
              {job.jobTitle}
            </Text>
          </Link>
          <Text className="truncate text-xs font-normal text-slate-500">
            {job.companyName}
          </Text>
        </Column>
        <EarningsCardAmounts
          commissionDollars={commissionDollars}
          commissionLabel="Commission per req"
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          revenueDollars={job.estimatedRevenueAmount || 0}
        />
      </Column>
    </Card>
  );

  return render();
};
